import { useSyncExternalStore } from "react";
import { addDays, diffDays, todayISO } from "@/engine/date";
import { recordWorkspaceAudit, resetWorkspaceAudit } from "./workspace-access";
import { getWorkspacePolicy } from "./workspace-policy";

export type MockMember = {
  id: string;
  name: string;
  email: string;
  role: string;
  status: string;
  joined: string;
  /** 없으면 마지막 활동 칸은 「—」. */
  lastActive?: string;
  guest?: boolean;
  /** 지정 초대의 만료일. 지나면 작업이 만료로 바꾼다. */
  expires?: string;
};

export type MockAuditRow = {
  id: string;
  when: string;
  who: string;
  what: string;
  /** 행을 열었을 때 보이는 설명. 지우기 위한 값이 아니다. */
  detail: string;
};

/** 감사 탭 목업. 서버에 저장하지 않고, 지우지도 않는다. */
export const MOCK_AUDIT_ROWS: MockAuditRow[] = [
  { id: "a1", when: "2026-09-28", who: "나", what: "멤버 초대", detail: "guest@client.example을 뷰어로 초대했습니다." },
  { id: "a2", when: "2026-09-20", who: "김PM", what: "공유 링크 만료", detail: "「현황 요약」의 만료를 바꿨습니다." },
  { id: "a3", when: "2026-08-02", who: "이아키", what: "플랜 이름 변경", detail: "플랜 이름을 「제조사 ERP 구축」으로 바꿨습니다." },
];

/** 사람·내용. 검색이 비면 모두 일치한다. */
export function auditMatchesQuery(row: Pick<MockAuditRow, "who" | "what">, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return row.who.toLowerCase().includes(q) || row.what.toLowerCase().includes(q);
}

const AUDIT_PERIOD_DAYS: Record<string, number> = { "7일": 7, "30일": 30, "90일": 90 };

/** 기간 필터. 「전체」는 모두 남긴다. 오늘은 포함하고, 미래 기록은 기간에 넣지 않는다. */
export function auditMatchesPeriod(row: Pick<MockAuditRow, "when">, period: string, today = todayISO()): boolean {
  if (period === "" || period === "전체") return true;
  const days = AUDIT_PERIOD_DAYS[period];
  if (days == null) return true;
  const age = diffDays(row.when, today);
  return age >= 0 && age <= days;
}

/** 행위 필터. 「전체」와 빈 값은 모두 남긴다. */
export function auditMatchesAction(row: Pick<MockAuditRow, "what">, action: string): boolean {
  return action === "" || action === "전체" || row.what === action;
}

/** 이름·이메일. 검색이 비면 모두 일치한다. */
export function memberMatchesQuery(person: Pick<MockMember, "name" | "email">, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return person.name.toLowerCase().includes(q) || person.email.toLowerCase().includes(q);
}

/** 역할 필터. 「전체」와 빈 값은 모두 남긴다. */
export function memberMatchesRole(person: Pick<MockMember, "role">, role: string): boolean {
  return role === "" || role === "전체" || person.role === role;
}

/** 상태 필터. 「전체」와 빈 값은 모두 남긴다. */
export function memberMatchesStatus(person: Pick<MockMember, "status">, status: string): boolean {
  return status === "" || status === "전체" || person.status === status;
}

/** 제거 확인의 영향. 서버가 센 개수가 없으면 숫자 없이 적는다. */
export function removalImpactSentence(): string {
  return "이 사람이 만든 공유 링크는 일시 중지되고, 액세스 토큰은 폐기됩니다. 다시 들어오려면 초대를 새로 보내야 합니다.";
}

/** 중지 확인의 영향. 멤버 자격은 남기고, 개수는 짓지 않는다. */
export function suspendImpactSentence(): string {
  return "이 사람의 공유 링크는 일시 중지되고, 액세스 토큰은 폐기됩니다. 멤버 자격은 남습니다.";
}

/** 재활성 확인. 멈추어 둔 공유는 여기서 다시 열지 않는다. */
export function reactivateImpactSentence(): string {
  return "멤버 자격이 다시 활성입니다. 멈추어 둔 공유 링크는 자동으로 열리지 않습니다.";
}

/** 철회 확인의 결과. 개수를 지어내지 않는다. */
export function revokeImpactSentence(): string {
  return "철회한 링크는 다시 열 수 없습니다.";
}

/** 일시 중지 확인. 개수를 지어내지 않는다. */
export function pauseImpactSentence(): string {
  return "중지한 동안은 이 링크를 열 수 없습니다.";
}

/** 접근 요청을 받을 때의 역할. 서버가 세지 않은 숫자는 넣지 않는다. */
export function acceptRequestImpact(role: string): string {
  return `${role}로 이 플랜에 들어옵니다.`;
}

/** 거절 뒤 같은 이메일은 바로 다시 요청하지 못한다. 대기 일수는 서버 값이라 적지 않는다. */
export function rejectRequestImpact(): string {
  return "같은 이메일은 다시 요청하기 전에 기다려야 합니다.";
}

/** 요청을 목록에서만 뺀다. 주소를 막거나 재요청을 기다리지 않는다. */
export function cancelRequestImpact(): string {
  return "멤버로 들어오지 않습니다.";
}

/** 차단 확인. 개수는 짓지 않는다. */
export function blockImpactSentence(): string {
  return "이 주소는 공용 링크로 다시 요청할 수 없습니다.";
}

/** 공용 초대 링크가 보이는 워크스페이스 이름. 멤버 수와 콘텐츠는 싣지 않는다. */
export const PUBLIC_INVITE_WORKSPACE = "웨이브엑스 SI";

/** 링크만으로는 멤버가 되지 않는다. */
export const PUBLIC_INVITE_NOTE = "링크로 온 사람은 접근 요청이 됩니다. 링크만으로는 멤버가 되지 않습니다.";

export const PUBLIC_INVITE_OFF = "공용 초대 링크가 꺼져 있습니다.";

/** 없거나 회전된 공용 링크. 워크스페이스 이름은 적지 않는다. */
export const PUBLIC_INVITE_CLOSED = "이 초대 링크는 더 이상 쓸 수 없습니다. 초대한 사람에게 다시 요청하세요.";

export const PUBLIC_INVITE_SENT = "접근 요청을 보냈습니다. 승인되면 뷰어로 들어옵니다.";

export const PUBLIC_INVITE_BLOCKED = "이 주소는 공용 링크로 요청할 수 없습니다.";

export const PUBLIC_INVITE_ALREADY = "이미 요청했거나 멤버입니다.";

/** 공용 링크 경로. 토큰 원문은 없다. */
export function publicInviteCopyText(id: string): string {
  return `/invite/t/${id}`;
}

export type PublicInviteLink = {
  id: string;
  enabled: boolean;
  retired: string[];
};

function freshPublicInvite(): PublicInviteLink {
  return { id: "ws", enabled: true, retired: [] };
}

/** 초대 재발송. 메일은 나가지 않고, 이전 링크는 바로 못 쓴다. */
export function resendImpactSentence(): string {
  return "이전 링크는 바로 쓸 수 없게 됩니다. 메일은 보내지 않습니다.";
}

/** 소유권 이전 확인. 재인증을 통과한 뒤에 보인다. */
export function transferOwnershipImpact(): string {
  return "이전하면 나는 관리자가 됩니다.";
}

/** T3 앞에 묻는 확인. 비밀번호는 받지 않고, 최근 로그인만 본다. */
export function reauthPromptSentence(): string {
  return "최근 5분 안에 로그인한 뒤에 진행할 수 있습니다.";
}

/** 최근 로그인이 아니면 위험 동작으로 넘어가지 않는다. */
export function reauthBlockSentence(): string {
  return "다시 로그인한 뒤에 진행할 수 있습니다.";
}

/** 플랜 나가기. 소유자는 이전한 뒤에만 나갈 수 있다. */
export function leaveImpactSentence(): string {
  return "다시 들어오려면 초대를 받아야 합니다.";
}

/** 소유자 역할은 나가지 못한다. */
export function canLeaveRole(role: string): boolean {
  return role !== "소유자" && role !== "owner";
}

/** 만들기 전 미리보기 캡션. */
export function sharePreviewCaption(): string {
  return "이 화면은 열람자에게 보이는 모습입니다.";
}

/** 고른 화면이 없으면 미리보기도 없다. 열람 페이지에는 아바타를 싣지 않는다. */
export function sharePreviewSummary(screens: readonly string[], mode: string): string | null {
  if (screens.length === 0) return null;
  return `${screens.join(" · ")} · 읽기 전용 · ${mode}`;
}

/** 역할 기본값으로 켜진 능력. 화면 칩은 금액·내보내기·링크만 보여 준다. */
export function capabilityChips(role: string): string[] {
  if (role === "소유자" || role === "관리자") return ["금액", "내보내기", "링크"];
  if (role === "편집자") return ["내보내기"];
  return [];
}

export type MockShareLink = {
  id: string;
  name: string;
  audience: string;
  expires: string;
  views: number;
  /** 보여 줄 화면. 예전 목업 행에는 없다. */
  screens?: string[];
  /** 최신 연동 또는 고정 발행. */
  mode?: string;
  /** 없으면 활성. 철회한 링크는 목록에서 빠진다. */
  status?: "활성" | "일시 중지";
};

export type MockShareDraft = {
  name: string;
  audience: string;
  mode: string;
  screens: string[];
};

/** 받은 초대의 범위. 워크스페이스 합류, 플랜 멤버십, 게스트(그 플랜만). */
export type IncomingInviteScope = "workspace" | "plan" | "guest";

/** 나에게 온 초대. 플랜 멤버 목록의 「초대 중」과 다르다. */
export type IncomingInvite = {
  id: string;
  scope: IncomingInviteScope;
  workspaceName: string;
  planName: string;
  role: string;
  /** 공개 플랜을 이미 읽는 멤버에게 역할을 더할 때. */
  alreadyReading?: boolean;
};

export type IncomingOutcome = "수락" | "거절";

/** 받은 함의 지난 결정. 대기 목록에서 빠진 뒤에 남긴다. */
export type IncomingDecision = {
  id: string;
  invite: IncomingInvite;
  outcome: IncomingOutcome;
};

const INITIAL_MEMBERS: MockMember[] = [
  { id: "me", name: "나", email: "me@wavex.local", role: "소유자", status: "활성", joined: "2026-03-02", lastActive: `${todayISO()}T09:00:00` },
  { id: "pm", name: "김PM", email: "pm@wavex.local", role: "관리자", status: "활성", joined: "2026-03-04", lastActive: `${addDays(todayISO(), -2)}T11:20:00` },
  { id: "arch", name: "이아키", email: "arch@wavex.local", role: "편집자", status: "활성", joined: "2026-04-11", lastActive: `${addDays(todayISO(), -12)}T16:00:00` },
];

/** 지정 초대가 스스로 만료되는 날 수. 화면에서 수동으로 만료하지 않는다. */
export const INVITE_TTL_DAYS = 7;

const INITIAL_INVITES: MockMember[] = [
  {
    id: "guest",
    name: "guest@client.example",
    email: "guest@client.example",
    role: "뷰어",
    status: "초대 중",
    joined: "2026-09-20",
    expires: addDays(todayISO(), INVITE_TTL_DAYS),
    guest: true,
  },
  { id: "inv-han", name: "한기획", email: "han.plan@partner.example", role: "편집자", status: "초대 중", joined: addDays(todayISO(), -1), expires: addDays(todayISO(), 1) },
  { id: "inv-oh", name: "오디자인", email: "oh.design@studio.example", role: "뷰어", status: "초대 중", joined: addDays(todayISO(), -2), expires: todayISO() },
  { id: "inv-seo", name: "서데이터", email: "seo.data@client.example", role: "뷰어", status: "거절", joined: addDays(todayISO(), -6), expires: addDays(todayISO(), 1) },
  { id: "inv-bae", name: "배클라우드", email: "bae.cloud@ops.example", role: "편집자", status: "만료", joined: addDays(todayISO(), -20), expires: addDays(todayISO(), -3) },
  { id: "inv-moon", name: "문프론트", email: "moon.ui@wavex.example", role: "뷰어", status: "초대 중", joined: addDays(todayISO(), -3), expires: addDays(todayISO(), INVITE_TTL_DAYS) },
  { id: "inv-yoon", name: "윤QA", email: "yoon.qa@qa.example", role: "관리자", status: "초대 중", joined: addDays(todayISO(), -1), expires: addDays(todayISO(), INVITE_TTL_DAYS) },
  { id: "inv-jo", name: "조보안", email: "jo.sec@client.example", role: "뷰어", status: "거절", joined: addDays(todayISO(), -8), expires: addDays(todayISO(), 2) },
  { id: "inv-nam", name: "남모바일", email: "nam.mobile@app.example", role: "편집자", status: "초대 중", joined: addDays(todayISO(), -4), expires: addDays(todayISO(), INVITE_TTL_DAYS) },
  { id: "inv-ra", name: "라번역", email: "ra.i18n@lang.example", role: "뷰어", status: "만료", joined: addDays(todayISO(), -18), expires: addDays(todayISO(), -1) },
  { id: "inv-cha", name: "차분석", email: "cha.analytics@data.example", role: "뷰어", status: "초대 중", joined: addDays(todayISO(), -2), expires: addDays(todayISO(), INVITE_TTL_DAYS) },
  { id: "inv-pyo", name: "표운영", email: "pyo.ops@run.example", role: "편집자", status: "초대 중", joined: addDays(todayISO(), -5), expires: addDays(todayISO(), INVITE_TTL_DAYS) },
];

const INITIAL_REQUESTS: MockMember[] = [
  { id: "req-yoon", name: "윤요청", email: "yoon@partner.example", role: "뷰어", status: "요청 중", joined: "2026-09-30" },
  { id: "req-moon", name: "문대기", email: "moon@client.example", role: "기여자", status: "요청 중", joined: "2026-09-29", guest: true },
];

/** 워크스페이스 공유 정책 목업. 최대 수명과 금액 금지. */
export const MOCK_SHARE_MAX_DAYS = 90;

export function sharePolicySentence(): string {
  return "최대 90일. 금액은 공유하지 않습니다.";
}

/** 만료가 정책의 최대 수명보다 멀면 규정 밖이다. */
export function shareExceedsPolicy(expires: string, today = todayISO(), maxDays = MOCK_SHARE_MAX_DAYS): boolean {
  return diffDays(today, expires) > maxDays;
}

/** 규정을 넘는 링크만 일시 중지한다. 이미 중지된 줄은 그대로 둔다. */
export function pauseOutOfPolicyShares(today = todayISO()): number {
  let count = 0;
  shares = shares.map((share) => {
    if (share.status === "일시 중지" || !shareExceedsPolicy(share.expires, today)) return share;
    count += 1;
    return { ...share, status: "일시 중지" as const };
  });
  if (count > 0) emit();
  return count;
}

/** 만료가 이 일수 안이면 임박이다. */
export const SHARE_EXPIRY_SOON_DAYS = 7;

export function shareExpirySoon(expires: string, today = todayISO()): boolean {
  const left = diffDays(today, expires);
  return left >= 0 && left <= SHARE_EXPIRY_SOON_DAYS;
}

/** 공유 목록 상태 필터. 검색은 이름만 본다. */
export const SHARE_LIST_FILTERS = ["전체", "활성", "일시 중지", "만료 임박", "규정 밖"] as const;

export function shareMatchesQuery(share: Pick<MockShareLink, "name">, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return share.name.toLowerCase().includes(q);
}

export type MockShareView = {
  id: string;
  when: string;
  device: string;
  audience: string;
};

/** 최근 열람. 기기 종류와 접근만 있고, 주소는 남기지 않는다. */
export function mockShareViews(id: string): MockShareView[] {
  if (id === "status") {
    return [
      { id: "status-1", when: "2026-09-30T14:20:00", device: "웹", audience: "비밀번호" },
      { id: "status-2", when: "2026-09-28T09:05:00", device: "웹", audience: "비밀번호" },
    ];
  }
  if (id === "schedule") {
    return [{ id: "schedule-1", when: "2026-09-29T11:40:00", device: "데스크톱", audience: "멤버 로그인" }];
  }
  if (id === "soon") {
    return [{ id: "soon-1", when: "2026-09-27T16:15:00", device: "웹", audience: "이메일 코드" }];
  }
  if (id === "long") {
    return [{ id: "long-1", when: "2026-09-12T08:30:00", device: "웹", audience: "비밀번호" }];
  }
  return [];
}

export function shareMatchesFilter(
  share: Pick<MockShareLink, "status" | "expires">,
  filter: string,
  today = todayISO(),
): boolean {
  if (filter === "활성") return share.status !== "일시 중지";
  if (filter === "일시 중지") return share.status === "일시 중지";
  if (filter === "만료 임박") return shareExpirySoon(share.expires, today);
  if (filter === "규정 밖") return shareExceedsPolicy(share.expires, today);
  return true;
}

const INITIAL_SHARES: MockShareLink[] = [
  { id: "status", name: "현황 요약", audience: "비밀번호", expires: addDays(todayISO(), 21), views: 12 },
  { id: "schedule", name: "일정 읽기", audience: "멤버 로그인", expires: addDays(todayISO(), 45), views: 3 },
  { id: "soon", name: "주간 점검", audience: "이메일 코드", expires: addDays(todayISO(), 3), views: 1 },
  { id: "long", name: "연간 일정", audience: "비밀번호", expires: addDays(todayISO(), 120), views: 4 },
];

const INITIAL_INCOMING: IncomingInvite[] = [
  { id: "in-ws", scope: "workspace", workspaceName: "웨이브엑스 SI", planName: "", role: "편집자" },
  { id: "in-quote", scope: "plan", workspaceName: "웨이브엑스 SI", planName: "견적 자동화", role: "편집자" },
  { id: "in-site", scope: "guest", workspaceName: "한빛건설", planName: "현장 일정", role: "뷰어" },
];

/** 워크스페이스 관리자가 콘텐츠를 보기로 한 목업. 세션 동안만 남는다. */
export type AdminGrant = {
  until: string;
  reason: string;
};

type Snapshot = {
  shareEnabled: boolean;
  members: MockMember[];
  invites: MockMember[];
  requests: MockMember[];
  shares: MockShareLink[];
  incoming: IncomingInvite[];
  incomingDecisions: IncomingDecision[];
  grants: Record<string, AdminGrant>;
  /** 이 세션에 쌓인 감사. 추가만 하고 지우지는 않는다. */
  audits: MockAuditRow[];
  /** 휴지통. 날짜는 고정이고, 자동으로 줄지 않는다. */
  trash: MockTrashItem[];
  /** 이 창에서 플랜을 나갔는지. 소유자 행은 남긴다. */
  departed: boolean;
  /** 워크스페이스 공용 초대 링크. 회전한 경로는 바로 끊긴다. */
  publicInvite: PublicInviteLink;
  blockedEmails: string[];
  cooledEmails: string[];
};

function freshAudits(): MockAuditRow[] {
  return MOCK_AUDIT_ROWS.map((row) => ({ ...row }));
}

/** 휴지통에 머무는 날. 화면의 예정일을 계산할 때만 쓴다. */
export const TRASH_HOLD_DAYS = 30;

export type MockTrashItem = {
  id: string;
  name: string;
  by: string;
  deleted: string;
  purge: string;
};

function freshTrash(today = todayISO()): MockTrashItem[] {
  const deleted = addDays(today, -27);
  return [{ id: "trash-quote", name: "견적 자동화", by: "나", deleted, purge: addDays(deleted, TRASH_HOLD_DAYS) }];
}

let shareEnabled = true;
let members = INITIAL_MEMBERS;
let invites = INITIAL_INVITES;
let requests = INITIAL_REQUESTS;
let shares = INITIAL_SHARES;
let incoming = INITIAL_INCOMING;
let incomingDecisions: IncomingDecision[] = [];
let grants: Record<string, AdminGrant> = {};
let audits = freshAudits();
let trash = freshTrash();
let departed = false;
let publicInvite = freshPublicInvite();
let blockedEmails: string[] = [];
let cooledEmails: string[] = [];
let snapshot: Snapshot = capture();
const listeners = new Set<() => void>();

function capture(): Snapshot {
  return { shareEnabled, members, invites, requests, shares, incoming, incomingDecisions, grants, audits, trash, departed, publicInvite, blockedEmails, cooledEmails };
}

/** 감사는 앞에 추가만 한다. 호출한 쪽이 emit 한다. */
function recordAudit(what: string, detail: string, who = "나") {
  audits = [{ id: `e${audits.length + 1}`, when: todayISO(), who, what, detail }, ...audits];
}

/** 플랜 감사와 워크스페이스 감사에 같은 결정을 남긴다. */
function noteDecision(what: string, detail: string, who = "나") {
  recordAudit(what, detail, who);
  recordWorkspaceAudit(what, detail, who);
}

function emit() {
  snapshot = capture();
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getMockPlanAccess(): Snapshot {
  return snapshot;
}

const STATUS_ROWS: MockMember[] = [
  { id: "st-pending", name: "박대기", email: "park@client.example", role: "뷰어", status: "초대 중", joined: "2026-09-28" },
  { id: "st-accepted", name: "김PM", email: "pm@wavex.local", role: "관리자", status: "수락", joined: "2026-03-04" },
  { id: "st-declined", name: "최거절", email: "choi@client.example", role: "편집자", status: "거절", joined: "2026-09-12" },
  { id: "st-expired", name: "정만료", email: "jung@client.example", role: "뷰어", status: "만료", joined: "2026-08-01" },
  { id: "st-revoked", name: "한취소", email: "han@client.example", role: "기여자", status: "취소", joined: "2026-09-02" },
  { id: "st-requested", name: "오요청", email: "oh@partner.example", role: "뷰어", status: "접근 요청", joined: "2026-09-30" },
  { id: "st-rejected", name: "서거절", email: "seo@partner.example", role: "뷰어", status: "요청 거절", joined: "2026-09-18" },
  { id: "st-blocked", name: "배차단", email: "bae@blocked.example", role: "뷰어", status: "차단", joined: "2026-07-20" },
];

export type MockAccessScene = "default" | "no-members" | "no-links" | "share-off" | "statuses" | "invites" | "no-invites" | "no-requests" | "expired-invite";

/** 목업 사례가 고른 화면으로 멤버·공유·받은 초대를 바꾼다. */
function resetPublicInvite() {
  publicInvite = freshPublicInvite();
  blockedEmails = [];
  cooledEmails = [];
}

export function applyMockAccessScene(scene: MockAccessScene) {
  departed = false;
  resetPublicInvite();
  if (scene === "default" || scene === "no-links" || scene === "share-off") {
    members = INITIAL_MEMBERS;
    invites = INITIAL_INVITES;
    requests = INITIAL_REQUESTS;
  }
  if (scene === "default") {
    shares = INITIAL_SHARES;
    shareEnabled = true;
  } else if (scene === "no-members") {
    members = [];
    invites = [];
    requests = [];
  } else if (scene === "no-links") {
    shares = [];
    shareEnabled = true;
  } else if (scene === "share-off") {
    shareEnabled = false;
  } else if (scene === "statuses") {
    members = STATUS_ROWS;
    invites = [];
    requests = INITIAL_REQUESTS;
  } else if (scene === "invites") {
    incoming = INITIAL_INCOMING;
    incomingDecisions = [];
  } else if (scene === "no-invites") {
    incoming = [];
  } else if (scene === "no-requests") {
    members = INITIAL_MEMBERS;
    invites = INITIAL_INVITES;
    requests = [];
  } else if (scene === "expired-invite") {
    members = INITIAL_MEMBERS;
    requests = INITIAL_REQUESTS;
    invites = [
      ...INITIAL_INVITES,
      {
        id: "old-invite",
        name: "old@client.example",
        email: "old@client.example",
        role: "뷰어",
        status: "초대 중",
        joined: addDays(todayISO(), -10),
        expires: addDays(todayISO(), -1),
        guest: true,
      },
    ];
    expireDueInvites();
  }
  emit();
}

export function resetMockPlanAccess() {
  shareEnabled = true;
  members = INITIAL_MEMBERS;
  invites = INITIAL_INVITES;
  requests = INITIAL_REQUESTS;
  shares = INITIAL_SHARES;
  incoming = INITIAL_INCOMING;
  incomingDecisions = [];
  grants = {};
  audits = freshAudits();
  trash = freshTrash();
  departed = false;
  resetPublicInvite();
  resetWorkspaceAudit();
  emit();
}

/** 켜진 현재 경로만 연다. 회전·끄기·없는 경로는 닫는다. */
export function publicInviteState(token: string): "open" | "closed" {
  if (!publicInvite.enabled || publicInvite.id !== token || publicInvite.retired.includes(token)) return "closed";
  return "open";
}

export function setPublicInviteEnabled(enabled: boolean) {
  publicInvite = { ...publicInvite, enabled };
  emit();
}

/** 이전 경로는 바로 못 쓴다. 새 경로에도 토큰 원문은 없다. */
export function rotatePublicInvite() {
  const retired = [...publicInvite.retired, publicInvite.id];
  publicInvite = { ...publicInvite, id: `ws-${retired.length + 1}`, retired };
  recordAudit("공용 링크 회전", "이전 초대 링크는 바로 쓸 수 없습니다.");
  emit();
}

export type PublicAccessResult = { ok: true } | { ok: false; reason: "형식" | "차단" | "대기" | "이미" };

/** 공용 링크 요청. 멤버로 넣지 않고, 역할은 뷰어만 된다. */
export function requestPublicAccess(email: string, today = todayISO()): PublicAccessResult {
  const normalized = email.trim().toLowerCase();
  if (!INVITE_EMAIL.test(normalized)) return { ok: false, reason: "형식" };
  const domain = normalized.slice(normalized.lastIndexOf("@") + 1);
  if (domain === "blocked.example" || blockedEmails.includes(normalized)) return { ok: false, reason: "차단" };
  if (cooledEmails.includes(normalized)) return { ok: false, reason: "대기" };
  const known = [...members, ...invites, ...requests];
  if (known.some((person) => person.email.trim().toLowerCase() === normalized)) return { ok: false, reason: "이미" };
  requests = [
    ...requests,
    {
      id: `req-${normalized.replace(/[^a-z0-9]+/g, "-")}`,
      name: normalized,
      email: normalized,
      role: "뷰어",
      status: "요청 중",
      joined: today,
      guest: domain !== "wavex.local",
    },
  ];
  recordAudit("접근 요청", `${normalized}을 공용 링크로 요청했습니다.`);
  emit();
  return { ok: true };
}

/** 소유자는 나가지 못한다. 다른 역할은 이 창에서만 떠난 것으로 둔다. */
export function leaveMockPlan(role: string): boolean {
  if (!canLeaveRole(role)) return false;
  if (!members.some((member) => member.role === "소유자")) return false;
  departed = true;
  emit();
  return true;
}

export function setMockShareEnabled(enabled: boolean) {
  shareEnabled = enabled;
  emit();
}

export function cancelMockInvite(id: string) {
  const invite = invites.find((item) => item.id === id);
  if (!invite) return;
  invites = invites.filter((item) => item.id !== id);
  noteDecision("초대 취소", `${invite.email} 초대를 취소했습니다.`);
  emit();
}

/** 활성 관리자에게 소유권을 넘긴다. 지금의 소유자는 관리자가 된다. */
export function transferMockOwnership(adminId: string) {
  const admin = members.find((member) => member.id === adminId && member.role === "관리자" && member.status === "활성");
  if (!admin) return;
  members = members.map((member) => {
    if (member.role === "소유자") return { ...member, role: "관리자" };
    if (member.id === adminId) return { ...member, role: "소유자" };
    return member;
  });
  emit();
}

function inviteTtlDays(): number {
  return getWorkspacePolicy().inviteDays;
}

/** 초대 중 칸의 둘째 줄. 0일이면 오늘까지. */
export function inviteRemainLabel(expires: string, today = todayISO()): string {
  const left = diffDays(today, expires);
  if (left <= 0) return "오늘까지";
  return `${left}일 남음`;
}

/** 상태는 그대로 두고 참여일과 만료만 오늘 기준으로 늘린다. 메일은 보내지 않는다. */
export function resendMockInvite(id: string, today = todayISO()) {
  const invite = invites.find((item) => item.id === id && item.status === "초대 중");
  if (!invite) return;
  const days = inviteTtlDays();
  invites = invites.map((item) => (item.id === id ? { ...item, joined: today, expires: addDays(today, days) } : item));
  noteDecision("초대 재발송", `${invite.email} 초대 만료를 ${days}일로 다시 잡았습니다.`);
  emit();
}

/** 거절·만료를 다시 초대 중으로 둔다. 만료는 오늘부터 다시 센다. */
export function reopenMockInvite(id: string, today = todayISO()) {
  const invite = invites.find((item) => item.id === id && (item.status === "거절" || item.status === "만료"));
  if (!invite) return;
  const days = inviteTtlDays();
  invites = invites.map((item) =>
    item.id === id ? { ...item, status: "초대 중", joined: today, expires: addDays(today, days) } : item,
  );
  noteDecision("다시 초대", `${invite.email}을 다시 초대했습니다.`);
  emit();
}

/** 만료일이 지난 초대 중만 만료로 바꾼다. 수동 만료는 없다. */
export function expireDueInvites(today = todayISO()) {
  const due = invites.filter((invite) => invite.status === "초대 중" && invite.expires && invite.expires < today);
  if (due.length === 0) return;
  const dueIds = new Set(due.map((invite) => invite.id));
  invites = invites.map((invite) => (dueIds.has(invite.id) ? { ...invite, status: "만료" } : invite));
  for (const invite of due) noteDecision("초대 만료", `${invite.email} 초대가 기간이 지나 만료됐습니다.`, "시스템");
  emit();
}

/** 수락하면 활성 멤버가 되고 요청 목록에서 빠진다. */
export function acceptMockAccessRequest(id: string) {
  const request = requests.find((item) => item.id === id);
  if (!request) return;
  requests = requests.filter((item) => item.id !== id);
  members = [...members, { ...request, status: "활성" }];
  emit();
}

export function rejectMockAccessRequest(id: string) {
  const request = requests.find((item) => item.id === id);
  requests = requests.filter((item) => item.id !== id);
  if (request) cooledEmails = [...cooledEmails, request.email.trim().toLowerCase()];
  emit();
}

/** 대기 중인 요청만 목록에서 뺀다. 멤버로 넣지 않고 주소를 막지 않는다. */
export function cancelMockAccessRequest(id: string) {
  const request = requests.find((item) => item.id === id && item.status === "요청 중");
  if (!request) return;
  requests = requests.filter((item) => item.id !== id);
  recordAudit("접근 요청 취소", `${request.email} 요청을 취소했습니다.`);
  emit();
}

/** 요청을 빼고 그 주소를 막는다. 멤버로 넣지 않는다. */
export function blockMockAccessRequest(id: string) {
  const request = requests.find((item) => item.id === id);
  if (!request) return;
  requests = requests.filter((item) => item.id !== id);
  blockedEmails = [...blockedEmails, request.email.trim().toLowerCase()];
  recordAudit("접근 차단", `${request.email}을 차단했습니다.`);
  emit();
}

export type PlanMemberDraft = {
  name: string;
  email: string;
  role: string;
  guest?: boolean;
};

/** 이메일이 이미 있으면 건너뛴다. 게스트는 초대 중으로 둔다. 넣은 사람 수를 반환한다. */
export function addMockPlanMembers(rows: PlanMemberDraft[]): number {
  const have = new Set(members.map((member) => member.email.trim().toLowerCase()));
  const next = [...members];
  let added = 0;
  for (const row of rows) {
    const email = row.email.trim();
    const key = email.toLowerCase();
    if (!email.includes("@") || have.has(key)) continue;
    have.add(key);
    added += 1;
    next.push({
      id: `dir-${key.replace(/[^a-z0-9]+/g, "-")}-${next.length}`,
      name: row.name.trim() || email,
      email,
      role: row.role,
      status: row.guest ? "초대 중" : "활성",
      joined: todayISO(),
      ...(row.guest ? { guest: true } : {}),
    });
  }
  if (!added) return 0;
  members = next;
  recordAudit("멤버 추가", `${added}명을 이 플랜에 넣었습니다.`);
  emit();
  return added;
}

/** 소유자는 빼지 않는다. */
export function removeMockMember(id: string) {
  members = members.filter((member) => member.id !== id || member.role === "소유자");
  emit();
}

/** 활성 멤버만 중지한다. 소유자는 그대로고, 공유 목록은 여기서 바꾸지 않는다. */
export function suspendMockMember(id: string) {
  members = members.map((member) =>
    member.id === id && member.role !== "소유자" && member.status === "활성" ? { ...member, status: "중지" } : member,
  );
  emit();
}

/** 중지된 멤버만 다시 활성으로 둔다. 공유 링크는 자동으로 열지 않는다. */
export function reactivateMockMember(id: string) {
  const member = members.find((item) => item.id === id && item.role !== "소유자" && item.status === "중지");
  if (!member) return;
  members = members.map((item) => (item.id === member.id ? { ...item, status: "활성" } : item));
  recordAudit("멤버 재활성", `${member.name}을 다시 활성으로 바꿨습니다.`);
  emit();
}

const ROLE_RANK = ["소유자", "관리자", "편집자", "기여자", "뷰어"] as const;

/** 내 역할 이하. 소유자 자리는 넘기지 않는다. */
export function assignableRoles(actorRole: string): string[] {
  const at = ROLE_RANK.indexOf(actorRole as (typeof ROLE_RANK)[number]);
  if (at < 0) return [];
  return ROLE_RANK.slice(Math.max(at, 1));
}

/** 초대 역할의 한 줄. 화면은 이 문장만 옆에 둔다. */
export function roleInviteLine(role: string): string {
  if (role === "관리자") return "멤버·공유를 관리합니다";
  if (role === "편집자") return "일정·작업을 편집합니다";
  if (role === "기여자") return "본인 작업의 진척과 실적만 입력합니다";
  if (role === "뷰어") return "이 플랜을 읽기만 합니다";
  return "";
}

export const INVITE_NO_MAIL = "메일을 보내지 않습니다. 링크를 직접 전달하세요.";

/** 이 창의 로그인 이메일 목업. 초대 주소와 다르면 수락하지 못한다. */
export const MOCK_SESSION_EMAIL = "me@wavex.local";

/** 초대 메일의 일부만 보여 준다. 도메인은 드러내지 않는다. */
export function maskEmail(email: string): string {
  const local = email.trim().split("@")[0] ?? "";
  const head = [...local][0] ?? "*";
  return `${head}***@…`;
}

export function inviteMismatchSentence(currentEmail: string): string {
  return `초대받은 이메일로 로그인해 주세요(현재: ${maskEmail(currentEmail)})`;
}

export const INVITE_ACCEPT_READY = "이 초대를 수락하면 들어올 수 있습니다.";
export const INVITE_ACCEPTED = "이 초대는 이미 수락되었습니다.";
export const INVITE_DECLINED = "이 초대를 거절했습니다.";
export const INVITE_EXPIRED = "이 초대는 만료되었습니다. 초대한 사람에게 다시 요청하세요.";
export const INVITE_REVOKED = "이 초대는 취소되었습니다. 초대한 사람에게 다시 요청하세요.";
export const INVITE_MISSING = "이 초대를 찾을 수 없습니다. 초대한 사람에게 다시 요청하세요.";

export function inviteJoinedSentence(workspace: string, role: string): string {
  return `${workspace}에 ${role}로 참여했습니다`;
}

/** 받은 초대에서 들어가는 대상. 워크스페이스는 공간 이름, 플랜·게스트는 플랜 이름. */
export function incomingInviteTitle(invite: IncomingInvite): string {
  return invite.scope === "workspace" ? invite.workspaceName : invite.planName;
}

/** 수락하면 무엇이 바뀌는지 한 줄. */
export function incomingInviteMeta(invite: IncomingInvite): string {
  if (invite.scope === "workspace") return invite.role;
  if (invite.scope === "guest") return `${invite.workspaceName} · 게스트 · 이 플랜만 · ${invite.role}`;
  if (invite.alreadyReading) return `${invite.workspaceName} · 이미 읽고 있습니다 · ${invite.role}`;
  return `${invite.workspaceName} · 이 플랜 · ${invite.role}`;
}

/** 수락 직후 그 자리에 남기는 문장. */
export function incomingJoinedSentence(invite: IncomingInvite): string {
  if (invite.scope === "workspace") return inviteJoinedSentence(invite.workspaceName, invite.role);
  if (invite.alreadyReading) return `${invite.planName}의 역할이 ${invite.role}로 바뀌었습니다`;
  if (invite.scope === "guest") return `${invite.planName}에 ${invite.role}로 참여했습니다. 이 플랜만 보입니다.`;
  return `${invite.planName}에 ${invite.role}로 참여했습니다`;
}

/** 로그인 뒤 돌아갈 경로. `/i/{id}`만 허용하고, 바깥 주소는 따라가지 않는다. */
export function safeInviteReturn(value: string | null | undefined): string | null {
  if (!value) return null;
  const raw = value.trim();
  if (!raw.startsWith("/i/")) return null;
  if (raw.includes("://") || raw.includes("//") || raw.includes("\\") || raw.includes("..")) return null;
  const id = raw.slice(3).split(/[?#]/)[0] ?? "";
  if (!id || id.includes("/")) return null;
  return `/i/${id}`;
}

export type DesignatedInviteView =
  | { kind: "missing" }
  | { kind: "mismatch"; current: string }
  | { kind: "pending"; role: string }
  | { kind: "accepted"; role: string }
  | { kind: "declined" }
  | { kind: "expired" }
  | { kind: "revoked" };

function inviteById(id: string): MockMember | undefined {
  return invites.find((invite) => invite.id === id);
}

/** 지정 초대. 실패에는 워크스페이스 이름을 싣지 않는다. `+tag`는 다른 주소다. */
export function designatedInviteView(id: string, sessionEmail = MOCK_SESSION_EMAIL): DesignatedInviteView {
  const invite = inviteById(id);
  if (!invite) return { kind: "missing" };
  if (invite.status === "만료" || (invite.status === "초대 중" && invite.expires && invite.expires < todayISO())) {
    return { kind: "expired" };
  }
  if (invite.status === "취소") return { kind: "revoked" };
  if (invite.status === "거절") return { kind: "declined" };
  if (invite.status === "수락") return { kind: "accepted", role: invite.role };
  if (invite.email.trim().toLowerCase() !== sessionEmail.trim().toLowerCase()) {
    return { kind: "mismatch", current: sessionEmail };
  }
  return { kind: "pending", role: invite.role };
}

export function keepHigherRole(current: string, incoming: string): string {
  const a = ROLE_RANK.indexOf(current as (typeof ROLE_RANK)[number]);
  const b = ROLE_RANK.indexOf(incoming as (typeof ROLE_RANK)[number]);
  if (a < 0) return incoming;
  if (b < 0) return current;
  return a <= b ? current : incoming;
}

/** 한 번만 수락한다. 이미 멤버면 역할을 낮추지 않는다. */
export function acceptDesignatedInvite(id: string, sessionEmail = MOCK_SESSION_EMAIL): DesignatedInviteView {
  const view = designatedInviteView(id, sessionEmail);
  if (view.kind !== "pending") return view;
  const invite = inviteById(id);
  if (!invite) return { kind: "missing" };
  const email = invite.email.trim().toLowerCase();
  const existing = members.find((member) => member.email.trim().toLowerCase() === email);
  if (existing) {
    members = members.map((member) =>
      member.id === existing.id ? { ...member, role: keepHigherRole(member.role, invite.role) } : member,
    );
  } else {
    members = [
      ...members,
      { ...invite, id: `m-${invite.id}`, status: "활성", name: invite.name },
    ];
  }
  invites = invites.map((item) => (item.id === id ? { ...item, status: "수락" } : item));
  recordAudit("초대 수락", `${invite.email}이 ${invite.role}로 참여했습니다.`);
  emit();
  return { kind: "accepted", role: invite.role };
}

export function declineDesignatedInvite(id: string, sessionEmail = MOCK_SESSION_EMAIL): DesignatedInviteView {
  const view = designatedInviteView(id, sessionEmail);
  if (view.kind !== "pending") return view;
  invites = invites.map((item) => (item.id === id ? { ...item, status: "거절" } : item));
  emit();
  return { kind: "declined" };
}

const INVITE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const INVITE_LIMIT = 50;

/** 쉼표·공백·줄바꿈으로 나누고, 같은 주소는 한 번만, 50개까지만 남긴다. */
export function splitInviteEmails(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of text.split(/[\s,]+/)) {
    const email = part.trim().toLowerCase();
    if (!email || seen.has(email)) continue;
    seen.add(email);
    out.push(email);
    if (out.length >= INVITE_LIMIT) break;
  }
  return out;
}

export type InviteChipIssue = "형식" | "이미 멤버" | "이미 초대" | "차단" | "도메인";

export type InviteChip = {
  email: string;
  issue: InviteChipIssue | null;
  guest: boolean;
};

/** 칩 하나. 허용 도메인이 있으면 그 밖은 막는다. 도메인을 비우면 워크스페이스 밖은 게스트다. */
export function classifyInviteEmail(email: string, people: Pick<MockMember, "email" | "status">[]): InviteChip {
  const normalized = email.trim().toLowerCase();
  if (!INVITE_EMAIL.test(normalized)) return { email: normalized, issue: "형식", guest: false };
  const domain = normalized.slice(normalized.lastIndexOf("@") + 1);
  if (domain === "blocked.example") return { email: normalized, issue: "차단", guest: false };
  const same = people.filter((person) => person.email.trim().toLowerCase() === normalized);
  if (same.some((person) => person.status === "활성" || person.status === "중지")) {
    return { email: normalized, issue: "이미 멤버", guest: false };
  }
  if (same.some((person) => person.status === "초대 중")) {
    return { email: normalized, issue: "이미 초대", guest: false };
  }
  const allowed = getWorkspacePolicy().domain.trim().toLowerCase();
  if (allowed && domain !== allowed) return { email: normalized, issue: "도메인", guest: false };
  return { email: normalized, issue: null, guest: domain !== "wavex.local" };
}

export function guestInviteSentence(email: string): string {
  return `${email}은 게스트로 초대됩니다. 이 플랜만 보입니다.`;
}

export function inviteIssueSentence(issue: InviteChipIssue): string {
  if (issue === "형식") return "형식이 아닙니다";
  if (issue === "이미 멤버") return "이미 멤버입니다";
  if (issue === "이미 초대") return "이미 초대된 주소입니다";
  if (issue === "도메인") return "허용하지 않는 도메인입니다";
  return "허용하지 않는 도메인입니다";
}

export type InviteResult = { id: string; email: string; role: string; guest: boolean };

/** 초대 링크. 토큰 원문은 없고 경로만 있다. */
export function inviteCopyText(id: string): string {
  return `/i/${id}`;
}

/** 이번 제출의 성공·실패 수. 실패 이유가 하나면 괄호에 적는다. */
export function inviteResultSummary(invited: number, failed: InviteChip[]): string {
  if (failed.length === 0) return `${invited}명 초대`;
  const issues = new Set(failed.map((chip) => chip.issue));
  if (issues.size === 1 && failed[0]?.issue) return `${invited}명 초대, ${failed.length}명 실패(${failed[0].issue})`;
  return `${invited}명 초대, ${failed.length}명 실패`;
}

/** 문제 없는 주소만 초대 중으로 넣는다. 메일은 보내지 않는다. */
export function submitMockInvites(emails: string[], role: string, today = todayISO()): { invited: InviteResult[]; failed: InviteChip[] } {
  const people = [...members, ...invites];
  const chips = emails.map((email) => classifyInviteEmail(email, people));
  const failed = chips.filter((chip) => chip.issue);
  const invited: InviteResult[] = chips
    .filter((chip) => !chip.issue)
    .map((chip) => ({
      id: `invite-${chip.email.replace(/[^a-z0-9]+/g, "-")}`,
      email: chip.email,
      role,
      guest: chip.guest,
    }));
  if (invited.length > 0) {
    invites = [
      ...invites,
      ...invited.map((item) => ({
        id: item.id,
        name: item.email,
        email: item.email,
        role: item.role,
        status: "초대 중",
        joined: today,
        expires: addDays(today, inviteTtlDays()),
        guest: item.guest,
      })),
    ];
    for (const item of invited) recordAudit("멤버 초대", `${item.email}을 ${item.role}로 초대했습니다.`);
    emit();
  }
  return { invited, failed };
}

export function isRoleDemotion(from: string, to: string): boolean {
  const a = ROLE_RANK.indexOf(from as (typeof ROLE_RANK)[number]);
  const b = ROLE_RANK.indexOf(to as (typeof ROLE_RANK)[number]);
  return a >= 0 && b > a;
}

const INVITE_ROLES = ["관리자", "편집자", "뷰어"] as const;

/** 초대 중만 역할을 바꾼다. 거절·만료는 그대로 둔다. */
export function setMockInviteRole(id: string, role: string): boolean {
  if (!INVITE_ROLES.includes(role as (typeof INVITE_ROLES)[number])) return false;
  const current = invites.find((item) => item.id === id && item.status === "초대 중");
  if (!current || current.role === role) return false;
  invites = invites.map((item) => (item.id === id ? { ...item, role } : item));
  noteDecision("역할 변경", `${current.name || current.email}의 역할을 ${role}로 바꿨습니다.`);
  emit();
  return true;
}

/** 소유자 역할은 바꾸지 않는다. 바뀐 역할만 감사에 남긴다. */
export function setMockMemberRole(id: string, role: string) {
  const current = members.find((member) => member.id === id) ?? invites.find((member) => member.id === id);
  const apply = (list: MockMember[]) =>
    list.map((member) => (member.id === id && member.role !== "소유자" ? { ...member, role } : member));
  members = apply(members);
  invites = apply(invites);
  if (current && current.role !== "소유자" && current.role !== role) {
    recordAudit("역할 변경", `${current.name}의 역할을 ${role}로 바꿨습니다.`);
  }
  emit();
}

export function revokeMockShare(id: string) {
  const share = shares.find((item) => item.id === id);
  if (!share) return;
  shares = shares.filter((item) => item.id !== id);
  recordAudit("공유 철회", `「${share.name}」 공유를 철회했습니다.`);
  emit();
}

/** 복사하는 주소. 비밀은 싣지 않는다. */
export function mockShareCopyText(id: string): string {
  return `/s/${id}`;
}

/** 열람 페이지 기준 시각. 서버 발행 시각이 아니라 목업이다. */
export const SHARE_VIEW_BASIS = "2026-10-01T08:30:00";

export const SHARE_VIEW_PLAN = "제조사 ERP 구축";

export const SHARE_VIEW_NOTICE = "이 링크의 열람은 기록됩니다.";

/** 없음·철회·만료·일시 중지가 같은 문장. 이유는 화면에 적지 않는다. */
export const SHARE_LINK_CLOSED = "이 링크는 지금 열 수 없습니다. 링크를 보낸 사람에게 문의하세요.";

/** 활성하고 만료일이 오늘 이전이 아닐 때만 연다. 없거나 철회된 링크는 닫는다. */
export function shareLinkOpen(
  share: { status?: string; expires?: string } | null | undefined,
  today = todayISO(),
): boolean {
  if (!share) return false;
  if (share.status === "일시 중지") return false;
  if (share.expires && diffDays(today, share.expires) < 0) return false;
  return true;
}

export type ShareViewRow = {
  id: string;
  name: string;
  start: string;
  end: string;
  progress: number;
};

export type ShareViewModel = {
  id: string;
  name: string;
  planName: string;
  basis: string;
  mode: string;
  screens: string[];
  rows: ShareViewRow[];
};

const SHARE_VIEW_ROWS: ShareViewRow[] = [
  { id: "req", name: "요구 정리", start: "2026-09-02", end: "2026-09-18", progress: 100 },
  { id: "design", name: "기본 설계", start: "2026-09-19", end: "2026-10-16", progress: 40 },
];

/** 목록에 화면이 없는 예전 목업은 이름에 맞는 기본 화면을 쓴다. */
export function shareViewScreens(share: Pick<MockShareLink, "id" | "screens">): string[] {
  if (share.screens && share.screens.length > 0) return [...share.screens];
  if (share.id === "schedule" || share.id === "long") return ["일정(간트)"];
  if (share.id === "soon") return ["현황 보고", "일정(간트)"];
  return ["현황 보고"];
}

/** 알려진 공유만 열람 화면이 있다. 닫힌 링크는 이유 없이 null이다. */
export function shareViewModel(
  id: string,
  shares: readonly (Pick<MockShareLink, "id" | "name" | "screens" | "mode"> & { status?: string; expires?: string })[],
): ShareViewModel | null {
  const share = shares.find((item) => item.id === id);
  if (!share || !shareLinkOpen(share)) return null;
  return {
    id: share.id,
    name: share.name,
    planName: SHARE_VIEW_PLAN,
    basis: SHARE_VIEW_BASIS,
    mode: share.mode === "고정 발행" ? "고정 발행" : "최신 연동",
    screens: shareViewScreens(share),
    rows: SHARE_VIEW_ROWS,
  };
}

/** 만료를 오늘부터 30일 뒤로 제안한다. */
export function proposedShareExpiry(today = todayISO()): string {
  return addDays(today, 30);
}

export function setMockShareExpiry(id: string, expires: string) {
  shares = shares.map((share) => (share.id === id ? { ...share, expires } : share));
  emit();
}

/** 아카이브 확인에 적는 공유 결과. 복구는 링크를 다시 열지 않는다. */
export function archiveShareSentence(): string {
  return "공유 링크는 일시 중지되고, 복구해도 자동으로 다시 열리지 않습니다.";
}

/** 아카이브할 때 공유를 모두 일시 중지한다. 복구는 이 함수를 부르지 않는다. */
export function pauseSharesOnArchive(): void {
  shares = shares.map((share) => ({ ...share, status: "일시 중지" as const }));
  recordAudit("플랜 아카이브", "플랜을 아카이브했습니다. 공유 링크는 일시 중지됩니다.");
  emit();
}

/** 휴지통에서 꺼낸다. 예정일은 그대로 두고, 자동으로 지우지는 않는다. */
export function restoreMockTrash(id: string): void {
  const item = trash.find((row) => row.id === id);
  if (!item) return;
  trash = trash.filter((row) => row.id !== id);
  recordAudit("휴지통 복구", `「${item.name}」을(를) 복구했습니다.`);
  emit();
}

export function pauseMockShare(id: string) {
  shares = shares.map((share) => (share.id === id ? { ...share, status: "일시 중지" } : share));
  emit();
}

export function resumeMockShare(id: string) {
  shares = shares.map((share) => (share.id === id ? { ...share, status: "활성" } : share));
  emit();
}

export function grantAdminAccess(planId: string, reason: string) {
  grants = { ...grants, [planId]: { until: addDays(todayISO(), 30), reason: reason.trim() } };
  emit();
}

export function endAdminAccess(planId: string) {
  if (!grants[planId]) return;
  const next = { ...grants };
  delete next[planId];
  grants = next;
  emit();
}

function settleIncoming(id: string, outcome: IncomingOutcome) {
  const invite = incoming.find((item) => item.id === id);
  if (!invite) return;
  incoming = incoming.filter((item) => item.id !== id);
  incomingDecisions = [{ id: `${id}-${outcome}`, invite, outcome }, ...incomingDecisions];
  emit();
}

export function acceptIncomingInvite(id: string) {
  settleIncoming(id, "수락");
}

export function declineIncomingInvite(id: string) {
  settleIncoming(id, "거절");
}

/** 휴지통 등으로 대상을 찾을 수 없는 초대를 대기 목록에서 지운다. */
export function dismissIncomingInvite(id: string) {
  if (!incoming.some((item) => item.id === id)) return;
  incoming = incoming.filter((item) => item.id !== id);
  emit();
}

/** 이름이 비거나 화면이 없으면 만들지 않는다. */
export function createMockShare(draft: MockShareDraft): boolean {
  const name = draft.name.trim();
  if (!name || draft.screens.length === 0) return false;
  const id = `share-${shares.length + 1}-${todayISO()}`;
  shares = [
    ...shares,
    {
      id,
      name,
      audience: draft.audience,
      expires: addDays(todayISO(), 30),
      views: 0,
      screens: draft.screens,
      mode: draft.mode,
    },
  ];
  recordAudit("공유 만들기", `「${name}」 공유를 만들었습니다.`);
  emit();
  return true;
}

export function addMockShare() {
  createMockShare({ name: "새 공유", audience: "비밀번호", mode: "최신 연동", screens: ["현황 보고"] });
}

export function useMockPlanAccess(): Snapshot {
  return useSyncExternalStore(subscribe, getMockPlanAccess, getMockPlanAccess);
}
